/**
 * The options schema — the shape a feature engine hands the UI so its tool's
 * controls can be rendered without the UI knowing what the tool does.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ ⚠ CONTRACT REQUEST, NOT A FROZEN CONTRACT.                               │
 * │                                                                          │
 * │ `contracts/api.md` §4 puts `tool.options_schema` in the tool-page        │
 * │ context and never says what is inside it. The five feature engines are   │
 * │ each supposed to deliver one, and none had arrived when this was         │
 * │ written. The alternative to proposing a shape was to build nothing, so   │
 * │ this file is the proposal: minimal, JSON-only, and covering every        │
 * │ control docs/USER_JOURNEY.md B3 actually names.                          │
 * │                                                                          │
 * │ It is escalated in comms/UI.md as a CONTRACT item. Until the CTO         │
 * │ ratifies or replaces it, the renderer in option-panel.ts is the only     │
 * │ consumer and the fixtures in web/static/tools/fixtures/ are the only     │
 * │ producers — so the blast radius of changing it is two files.             │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * Four properties it is designed around, each from a stated requirement:
 *
 *   1. **Genuine nesting.** keep-italic / keep-bold / keep-font sit UNDER
 *      "remove formatting" — as `children`, not as a flat list with a naming
 *      convention. Nesting is where the competitor's cleaner UI gets confusing
 *      and it is the tool we most want to beat.
 *   2. **Framerate never defaults.** `framerate` fields have no default value
 *      by construction: `default` is typed `null` and nothing else. Defaulting
 *      to 23.976 is wrong for every PAL source and is the exact competitor bug
 *      this product exists to beat.
 *   3. **Unlimited repeats.** Per-range shift needs "unlimited rows", so
 *      `repeat.max` is `number | null` and null means null.
 *   4. **Inert JSON.** Ops cross `postMessage` as plain JSON by contract, and
 *      so does this. No functions, no regexes, no dates — a schema that needs
 *      a function to express a constraint is a schema that cannot be sent from
 *      a worker or written to a fixture.
 */

import type { Alignment, Color } from '../../../../../contracts/cue-model.ts';

export type OptionValue =
  | string
  | number
  | boolean
  | null
  | OptionValue[]
  | { [k: string]: OptionValue };

export interface FieldBase {
  /** The op parameter name. Unique across the whole schema, including inside repeats. */
  readonly key: string;
  readonly label: string;
  /** One line under the control. Not a tooltip: this site has no hover-only affordances. */
  readonly help?: string;
  /** Options that only make sense while this one is on. Rendered indented, under it. */
  readonly children?: readonly OptionField[];
  /**
   * Values of THIS field that enable its children. Defaults to `[true]`, which
   * is what a checkbox parent means. A select parent lists the modes that
   * unlock its children.
   */
  readonly childrenWhen?: readonly (string | number | boolean)[];
  /**
   * Keys of OTHER fields, any one of which enables this one, each by the
   * rule a parent unlocks its children (`childrenEnabled`): a switch that is
   * on, a number box holding a number. Disabled while none of them does.
   *
   * For a control that governs more than one field's work, which a single
   * parent cannot express. /fix-subtitle-timing's "When two cues overlap" is
   * also how the minimum gap is made, so as a child of "Repair overlapping
   * cues" it was greyed out while it still decided the gap. Top-level fields
   * only: a child is enabled by its parent, and `validateSchema` refuses both
   * at once.
   *
   * An enabler in ANOTHER group takes both groups out of the phone accordion
   * (`accordionMembers` in option-panel.ts), so the key and the field it
   * enables can be open together.
   */
  readonly enabledBy?: readonly string[];
}

export interface BooleanField extends FieldBase {
  readonly type: 'boolean';
  readonly default: boolean;
}

export interface NumberField extends FieldBase {
  readonly type: 'number';
  readonly default: number | null;
  readonly min?: number;
  readonly max?: number;
  readonly step?: number;
  /** Rendered beside the input: 'ms', 'chars', 'cps'. */
  readonly unit?: string;
}

export interface TextField extends FieldBase {
  readonly type: 'text';
  readonly default: string;
  readonly placeholder?: string;
}

export interface Choice {
  readonly value: string;
  readonly label: string;
  readonly help?: string;
}

export interface SelectField extends FieldBase {
  readonly type: 'select';
  readonly default: string;
  readonly choices: readonly Choice[];
}

/** Rendered as radios when there are few enough choices to show them all. */
export interface RadioField extends FieldBase {
  readonly type: 'radio';
  readonly default: string;
  readonly choices: readonly Choice[];
}

/**
 * A time offset accepting milliseconds OR `hh:mm:ss.mmm`, per USER_JOURNEY B3.
 * The value is always milliseconds; the two input syntaxes are a parsing
 * concern in the renderer, not two fields.
 */
export interface DurationField extends FieldBase {
  readonly type: 'duration';
  readonly default: number;
  readonly allowNegative?: boolean;
}

/**
 * Framerate. `default` is `null` and the type says so — see property 2 above.
 * `choices` are offered as a convenience; the field also accepts a typed
 * rational, because 24000/1001 is not 23.976.
 */
export interface FramerateField extends FieldBase {
  readonly type: 'framerate';
  readonly default: null;
  readonly choices?: readonly Choice[];
}

/**
 * The 9-cell position grid, value `Alignment | null`.
 *
 * `null` is "clear, inherit from the style" and is genuinely NOT a select's
 * empty string: the op distinguishes "put it bottom-centre" from "do not set a
 * position", and collapsing the two would make the clear state unreachable.
 * `docs/CUE_MODEL.md`: the product's 9-cell grid is exactly `Alignment`, and no
 * second vocabulary exists.
 */
export interface Grid9Field extends FieldBase {
  readonly type: 'grid9';
  readonly default: Alignment | null;
}

/**
 * A colour, value `{r, g, b, a}` — NEVER a hex string.
 *
 * The contract separated structured colour out on purpose: *"string colours
 * make equality case-sensitive, which silently breaks dedupe, style coalescing
 * after a strip, and the colour transform."* A hex string at this seam would
 * re-introduce that bug at the one boundary nobody tests, so the picker parses
 * to channels here and a hex string never enters the value.
 *
 * `a` is 0–1 with 1 = opaque, deliberately inverted from ASS so a sign error
 * cannot leak into the model.
 */
export interface ColorField extends FieldBase {
  readonly type: 'color';
  readonly default: Color | null;
  /** Offer an alpha control. Most tools want opaque-only. */
  readonly alpha?: boolean;
}

/** A repeating row group — the per-range shift editor's "unlimited rows". */
export interface RepeatField extends FieldBase {
  readonly type: 'repeat';
  readonly fields: readonly OptionField[];
  readonly min?: number;
  /** null means unlimited, and is the default. */
  readonly max?: number | null;
  readonly addLabel: string;
  /** Used for each row's accessible name: "{rowLabel} 1", "{rowLabel} 2". */
  readonly rowLabel: string;
  readonly default?: readonly Record<string, OptionValue>[];
}

export type OptionField =
  | BooleanField
  | Grid9Field
  | ColorField
  | NumberField
  | TextField
  | SelectField
  | RadioField
  | DurationField
  | FramerateField
  | RepeatField;

export interface OptionGroup {
  readonly key: string;
  readonly label: string;
  readonly help?: string;
  readonly fields: readonly OptionField[];
  /**
   * Open on first render, and kept out of the phone accordion
   * (`accordionMembers` in option-panel.ts): the group holding the tool's
   * main input, or both groups when that input is split in two, as
   * /sync-subtitles' first and last points are. A group joined to another
   * by `enabledBy` is kept out too, without being opened.
   */
  readonly open?: boolean;
}

export interface OptionsSchema {
  readonly version: 1;
  readonly groups: readonly OptionGroup[];
}

export type OptionValues = Record<string, OptionValue>;

/**
 * Every field type this renderer can actually draw.
 *
 * `validateSchema` checks against THIS, and that check is the whole point of
 * the schema being validated by its consumer: a producer can only ship a
 * control the renderer has. Without it an unknown type passed validation and
 * `option-panel.ts` drew an em-dash with a console.error — so STRUCTURE's grid9
 * and color tool pages would have shipped without their main control, with
 * nothing failing anywhere. STRUCTURE pinned that as a deliberately-failing
 * KNOWN GAP test; this closes it.
 */
export const FIELD_TYPES = [
  'boolean',
  'number',
  'text',
  'select',
  'radio',
  'duration',
  'framerate',
  'grid9',
  'color',
  'repeat',
] as const;

export type FieldType = (typeof FIELD_TYPES)[number];

const KNOWN_TYPES: ReadonlySet<string> = new Set(FIELD_TYPES);

/* ── defaults ─────────────────────────────────────────────────────────── */

function fieldDefault(field: OptionField): OptionValue {
  switch (field.type) {
    case 'repeat':
      return (field.default ?? []) as OptionValue;
    case 'framerate':
      // Never a number. A frame-based read or write with no framerate fails
      // closed, and this is where that starts.
      return null;
    case 'grid9':
      // Alignment | null, and null is a real value meaning "inherit".
      return field.default as OptionValue;
    case 'color':
      return (field.default ?? null) as OptionValue;
    default:
      return field.default as OptionValue;
  }
}

/** Walk a schema depth-first, children included. */
export function* walkFields(schema: OptionsSchema): Generator<OptionField> {
  const stack: OptionField[] = [];
  for (const group of schema.groups) stack.push(...group.fields);
  while (stack.length > 0) {
    const field = stack.pop();
    if (!field) continue;
    yield field;
    if (field.children) stack.push(...field.children);
    if (field.type === 'repeat') stack.push(...field.fields);
  }
}

/** The value object a schema starts from. */
export function defaultValues(schema: OptionsSchema): OptionValues {
  const values: OptionValues = {};
  for (const group of schema.groups) collectDefaults(group.fields, values);
  return values;
}

function collectDefaults(fields: readonly OptionField[], into: OptionValues): void {
  for (const field of fields) {
    if (field.type !== 'repeat') into[field.key] = fieldDefault(field);
    else into[field.key] = fieldDefault(field);
    if (field.children) collectDefaults(field.children, into);
  }
}

/**
 * True when a parent's current value unlocks its children.
 *
 * A NUMBER parent with no `childrenWhen` is unlocked by having a number in it.
 * The default `[true]` is a switch's rule and a number box never holds `true`,
 * so on /fix-subtitle-timing "Grow short cues" and "Never grow a cue into its
 * neighbour", the two children of "Minimum time on screen", were disabled
 * whatever was typed: the op read them and no user could change them. An empty
 * box is `null`, the op's own "off", and keeps them disabled.
 */
export function childrenEnabled(field: OptionField, value: OptionValue): boolean {
  if (field.childrenWhen === undefined && (field.type === 'number' || field.type === 'duration')) {
    return typeof value === 'number';
  }
  const allowed = field.childrenWhen ?? [true];
  return allowed.some((v) => v === value);
}

/**
 * True when a field with `enabledBy` has at least one enabler that unlocks
 * it, and always true for a field without one. The panel greys a field out
 * by this, so a page's rule and a test's are the same function.
 */
export function enabledByOthers(
  field: OptionField,
  schema: OptionsSchema,
  values: OptionValues,
): boolean {
  if (field.enabledBy === undefined || field.enabledBy.length === 0) return true;
  const byKey = new Map([...walkFields(schema)].map((f) => [f.key, f]));
  return field.enabledBy.some((key) => {
    const by = byKey.get(key);
    return by !== undefined && childrenEnabled(by, values[key] ?? null);
  });
}

/**
 * Whether the panel lets the user change `key` with these values: every
 * parent above it unlocks its children, and its `enabledBy`, if any, is met.
 * `false` for a key the schema does not have.
 */
export function fieldEnabled(schema: OptionsSchema, key: string, values: OptionValues): boolean {
  const find = (
    fields: readonly OptionField[],
    parentsOn: boolean,
  ): { field: OptionField; on: boolean } | null => {
    for (const field of fields) {
      if (field.key === key) return { field, on: parentsOn };
      const own = parentsOn && childrenEnabled(field, values[field.key] ?? null);
      const below = field.children ? find(field.children, own) : null;
      if (below !== null) return below;
    }
    return null;
  };
  for (const group of schema.groups) {
    const hit = find(group.fields, true);
    if (hit !== null) return hit.on && enabledByOthers(hit.field, schema, values);
  }
  return false;
}

/**
 * Reject a schema the renderer cannot draw, loudly, at mount.
 *
 * A tool whose options cannot be rendered sensibly is a conversation between
 * the CTO and that feature — not something the UI absorbs by drawing an
 * approximation. Returns the problems; an empty array means it is renderable.
 */
export function validateSchema(schema: unknown): string[] {
  const problems: string[] = [];
  if (typeof schema !== 'object' || schema === null) return ['schema is not an object'];
  const s = schema as Partial<OptionsSchema>;
  if (s.version !== 1) problems.push(`unsupported schema version: ${String(s.version)}`);
  if (!Array.isArray(s.groups)) return [...problems, 'schema.groups is not an array'];

  const seen = new Set<string>();
  // Checked once every key is known, because an enabler may sit in a later group.
  const enablers: { path: string; refs: readonly unknown[] }[] = [];
  const visit = (fields: readonly OptionField[], path: string, nested = false) => {
    for (const field of fields) {
      if (!field || typeof field.key !== 'string' || field.key === '') {
        problems.push(`${path}: a field has no key`);
        continue;
      }
      if (seen.has(field.key)) problems.push(`${path}.${field.key}: duplicate key`);
      seen.add(field.key);
      if (typeof field.label !== 'string' || field.label === '') {
        problems.push(`${path}.${field.key}: no label`);
      }
      // THE CHECK THAT WAS MISSING. An unknown type used to pass here and be
      // drawn as an em-dash: a tool page shipping without its main control,
      // silently. A refusal at mount is the only honest outcome — the schema's
      // producer learns immediately that this build cannot draw their field.
      if (!KNOWN_TYPES.has(field.type as string)) {
        problems.push(
          `${path}.${field.key}: unknown field type "${String(field.type)}" — this build can draw ` +
            FIELD_TYPES.join(', '),
        );
      }
      // `FramerateField.default` is typed `null`, so the compiler treats this
      // branch as unreachable — which is the point. The check is here because
      // this function validates data that arrived from a feature engine at
      // runtime, where the type says nothing.
      if (field.type === 'framerate' && (field as { default: unknown }).default !== null) {
        problems.push(`${path}.${field.key}: a framerate field must not carry a default`);
      }
      if ((field.type === 'select' || field.type === 'radio') && field.choices.length === 0) {
        problems.push(`${path}.${field.key}: no choices`);
      }
      // A misspelt enabler would leave its field greyed out for good, with
      // nothing failing anywhere; a child's enabler would fight its parent's
      // refresh over the same inputs. Both are refused here instead.
      if (field.enabledBy !== undefined) {
        if (nested) {
          problems.push(`${path}.${field.key}: enabledBy on a child, which its parent enables`);
        }
        enablers.push({ path: `${path}.${field.key}`, refs: field.enabledBy });
      }
      if (field.children) visit(field.children, `${path}.${field.key}`, true);
      if (field.type === 'repeat') visit(field.fields, `${path}.${field.key}[]`, true);
    }
  };
  for (const group of s.groups) {
    if (!group || typeof group.key !== 'string') {
      problems.push('a group has no key');
      continue;
    }
    visit(group.fields ?? [], group.key);
  }
  for (const { path, refs } of enablers) {
    for (const ref of refs) {
      if (typeof ref !== 'string' || !seen.has(ref)) {
        problems.push(`${path}: enabledBy names "${String(ref)}", which is not a field here`);
      }
    }
  }
  return problems;
}
